// path: src/Components/story/StoryBoardMap.js

import React, { useEffect, useMemo, useState } from "react";
import {
    getStoryLevelById,
    getStoryLevelsForMapPage,
    getStoryMainLevelCount,
} from "../../data/storyLevels";
import {
    getStoryLevelLockHint,
    isStoryHardModeActive,
    isStoryLevelCompletedOnActiveTrack,
    isStoryLevelUnlocked,
    isUnderworldUnlocked,
} from "../../utils/storyProgress";

/**
 * Stage circle centers — shared by the visible markers and click targets.
 * Rows share one top value; columns share one left value so stages form a grid.
 * Serpentine path bottom→top: 1-2-3, 4-5-6, 7-8-9, 10-11-12.
 * Percents are relative to the full-bleed board (art under open chrome).
 * Columns match the original wide layout; row step is 9.4%.
 * Cluster is vertically centered so space above 10–12 equals space below 1–3.
 *
 * Underworld uses the same row step with five rows (19, branch, 15, 14, 13)
 * centered on the board so top/bottom margins match.
 */
const STAGE_COLUMN_LEFT = {
    left: 29.1,
    middle: 51.1,
    right: 73.1,
};

const STAGE_ROW_TOP = {
    beach: 64.1,
    meadow: 54.7,
    mountains: 45.3,
    space: 35.9,
};

const STAGE_HOTSPOTS = {
    1: { left: STAGE_COLUMN_LEFT.left, top: STAGE_ROW_TOP.beach },
    2: { left: STAGE_COLUMN_LEFT.middle, top: STAGE_ROW_TOP.beach },
    3: { left: STAGE_COLUMN_LEFT.right, top: STAGE_ROW_TOP.beach },
    4: { left: STAGE_COLUMN_LEFT.right, top: STAGE_ROW_TOP.meadow },
    5: { left: STAGE_COLUMN_LEFT.middle, top: STAGE_ROW_TOP.meadow },
    6: { left: STAGE_COLUMN_LEFT.left, top: STAGE_ROW_TOP.meadow },
    7: { left: STAGE_COLUMN_LEFT.left, top: STAGE_ROW_TOP.mountains },
    8: { left: STAGE_COLUMN_LEFT.middle, top: STAGE_ROW_TOP.mountains },
    9: { left: STAGE_COLUMN_LEFT.right, top: STAGE_ROW_TOP.mountains },
    10: { left: STAGE_COLUMN_LEFT.right, top: STAGE_ROW_TOP.space },
    11: { left: STAGE_COLUMN_LEFT.middle, top: STAGE_ROW_TOP.space },
    12: { left: STAGE_COLUMN_LEFT.left, top: STAGE_ROW_TOP.space },
};

/** Underworld: 13–15 vertical (south→north), then 16–18 row, boss 19 above. */
const UNDERWORLD_ROW_STEP = 9.4;
const UNDERWORLD_ROW_COUNT = 5;
/** Nudge down so gap to map tabs matches gap to level-details panel. */
const UNDERWORLD_CLUSTER_NUDGE = 2.2;
const UNDERWORLD_TOP_ROW =
    (100 - (UNDERWORLD_ROW_COUNT - 1) * UNDERWORLD_ROW_STEP) / 2 +
    UNDERWORLD_CLUSTER_NUDGE;

const UNDERWORLD_ROW_TOP = {
    boss: UNDERWORLD_TOP_ROW,
    branch: UNDERWORLD_TOP_ROW + UNDERWORLD_ROW_STEP,
    gate: UNDERWORLD_TOP_ROW + UNDERWORLD_ROW_STEP * 2,
    mid: UNDERWORLD_TOP_ROW + UNDERWORLD_ROW_STEP * 3,
    entry: UNDERWORLD_TOP_ROW + UNDERWORLD_ROW_STEP * 4,
};

const UNDERWORLD_HOTSPOTS = {
    19: { left: 50.0, top: UNDERWORLD_ROW_TOP.boss },
    16: { left: 28.0, top: UNDERWORLD_ROW_TOP.branch },
    17: { left: 50.0, top: UNDERWORLD_ROW_TOP.branch },
    18: { left: 72.0, top: UNDERWORLD_ROW_TOP.branch },
    15: { left: 50.0, top: UNDERWORLD_ROW_TOP.gate },
    14: { left: 50.0, top: UNDERWORLD_ROW_TOP.mid },
    13: { left: 50.0, top: UNDERWORLD_ROW_TOP.entry },
};

function StoryBoardLockIcon() {
    return (
        <span className="storyBoardHotspot__lock" aria-hidden="true">
            <svg
                className="storyBoardHotspot__lockIcon"
                viewBox="0 0 24 24"
                focusable="false"
            >
                <path
                    d="M7.5 10.25V8.1a4.5 4.5 0 0 1 9 0v2.15"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                />
                <rect
                    x="5.25"
                    y="10.25"
                    width="13.5"
                    height="10"
                    rx="2.4"
                    fill="currentColor"
                />
                <circle cx="12" cy="14.6" r="1.35" fill="#2a3144" />
                <path
                    d="M12 15.7v2.1"
                    fill="none"
                    stroke="#2a3144"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                />
            </svg>
        </span>
    );
}

function getHotspotForLevel(levelId, mapPage) {
    if (mapPage === "underworld") {
        return UNDERWORLD_HOTSPOTS[levelId] || null;
    }

    return STAGE_HOTSPOTS[levelId] || null;
}

export default function StoryBoardMap({
    progress,
    onPlayLevel,
    selectedLevelId = null,
    onSelectLevel,
    header = null,
    initialMapPage = "surface",
}) {
    const [internalSelectedId, setInternalSelectedId] = useState(1);
    const [mapPage, setMapPage] = useState(initialMapPage);

    const underworldAvailable = isUnderworldUnlocked(progress);
    const isUnderworldPage = mapPage === "underworld";
    const hardModeActive = isStoryHardModeActive(progress);
    const visibleLevels = useMemo(
        () => getStoryLevelsForMapPage(mapPage),
        [mapPage]
    );

    const selectedId =
        selectedLevelId != null ? selectedLevelId : internalSelectedId;

    const selectedLevel = useMemo(
        () => getStoryLevelById(selectedId),
        [selectedId]
    );

    const selectedStatus = useMemo(() => {
        if (!selectedLevel) {
            return "locked";
        }

        if (isStoryLevelCompletedOnActiveTrack(selectedLevel.id, progress)) {
            return "completed";
        }

        if (isStoryLevelUnlocked(selectedLevel.id, progress)) {
            return "available";
        }

        return "locked";
    }, [selectedLevel, progress]);

    useEffect(() => {
        if (initialMapPage === "underworld" && underworldAvailable) {
            setMapPage("underworld");
        }
    }, [initialMapPage, underworldAvailable]);

    useEffect(() => {
        if (!selectedLevel || getHotspotForLevel(selectedLevel.id, mapPage)) {
            return;
        }

        const fallbackLevel = visibleLevels[0];
        if (!fallbackLevel) {
            return;
        }

        if (typeof onSelectLevel === "function") {
            onSelectLevel(fallbackLevel.id);
        } else {
            setInternalSelectedId(fallbackLevel.id);
        }
    }, [mapPage, onSelectLevel, selectedLevel, visibleLevels]);

    const selectLevel = (levelId) => {
        if (typeof onSelectLevel === "function") {
            onSelectLevel(levelId);
        } else {
            setInternalSelectedId(levelId);
        }
    };

    const handleHotspotActivate = (levelId) => {
        selectLevel(levelId);
    };

    const handlePlaySelected = () => {
        if (
            !selectedLevel ||
            selectedStatus === "locked" ||
            typeof onPlayLevel !== "function"
        ) {
            return;
        }

        onPlayLevel(selectedLevel.id);
    };

    const openUnderworld = () => {
        if (!underworldAvailable) {
            return;
        }

        setMapPage("underworld");
        selectLevel(13);
    };

    const returnToSurface = () => {
        setMapPage("surface");
        selectLevel(getStoryMainLevelCount());
    };

    return (
        <div
            className={`storyBoardMap${
                isUnderworldPage ? " storyBoardMap--underworld" : ""
            }`}
        >
            {!isUnderworldPage ? (
                <img
                    className="storyBoardMap__image"
                    src={`${process.env.PUBLIC_URL}/images/story-mode-board-v7.jpg`}
                    alt=""
                    aria-hidden="true"
                    decoding="async"
                />
            ) : (
                <img
                    className="storyBoardMap__image"
                    src={`${process.env.PUBLIC_URL}/images/story-mode-underworld-board-v6.jpg`}
                    alt=""
                    aria-hidden="true"
                    decoding="async"
                />
            )}

            <div
                className={`storyBoardMap__stages${
                    isUnderworldPage ? " storyBoardMap__stages--underworld" : ""
                }`}
                role="group"
                aria-label={
                    isUnderworldPage
                        ? "Story Mode Underworld bonus stages"
                        : "Story Mode adventure board"
                }
            >
                {visibleLevels.map((level) => {
                    const hotspot = getHotspotForLevel(level.id, mapPage);
                    if (!hotspot) {
                        return null;
                    }

                    const completed = isStoryLevelCompletedOnActiveTrack(
                        level.id,
                        progress
                    );
                    const unlocked = isStoryLevelUnlocked(level.id, progress);
                    const status = completed
                        ? "completed"
                        : unlocked
                        ? "available"
                        : "locked";
                    const isSelected = selectedId === level.id;

                    return (
                        <button
                            key={level.id}
                            type="button"
                            className={`storyBoardHotspot storyBoardHotspot--${status}${
                                isSelected
                                    ? " storyBoardHotspot--selected"
                                    : ""
                            }${
                                isUnderworldPage
                                    ? " storyBoardHotspot--underworld"
                                    : ""
                            }${
                                level.id === 19
                                    ? " storyBoardHotspot--boss"
                                    : ""
                            }`}
                            style={{
                                left: `${hotspot.left}%`,
                                top: `${hotspot.top}%`,
                            }}
                            aria-label={`Story Level ${level.id}: ${
                                level.title
                            }. ${
                                completed
                                    ? hardModeActive
                                        ? "Hard Mode cleared"
                                        : "Completed"
                                    : unlocked
                                    ? "Available"
                                    : "Locked"
                            }`}
                            aria-pressed={isSelected}
                            aria-disabled={!unlocked}
                            onClick={() => handleHotspotActivate(level.id)}
                        >
                            <span
                                className="storyBoardHotspot__number"
                                aria-hidden="true"
                            >
                                {level.id}
                            </span>
                            {!unlocked && <StoryBoardLockIcon />}
                            {completed && (
                                <span
                                    className="storyBoardHotspot__check"
                                    aria-hidden="true"
                                >
                                    ✓
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            <div className="storyBoardMap__chrome">
                {header}

                <div
                    className="storyBoardMap__pageNav"
                    aria-label="Story map pages"
                >
                    <button
                        type="button"
                        className={`storyBoardMap__pageTab${
                            !isUnderworldPage
                                ? " storyBoardMap__pageTab--active"
                                : ""
                        }`}
                        onClick={returnToSurface}
                        aria-pressed={!isUnderworldPage}
                    >
                        Overworld
                    </button>
                    <button
                        type="button"
                        className={`storyBoardMap__pageTab${
                            isUnderworldPage
                                ? " storyBoardMap__pageTab--active"
                                : ""
                        }${
                            underworldAvailable
                                ? ""
                                : " storyBoardMap__pageTab--locked"
                        }`}
                        onClick={openUnderworld}
                        aria-pressed={isUnderworldPage}
                        aria-disabled={!underworldAvailable}
                        title={
                            underworldAvailable
                                ? "Enter the Underworld"
                                : `Clear Story Level ${getStoryMainLevelCount()} to unlock`
                        }
                    >
                        Underworld
                    </button>
                </div>

                <div className="storyBoardMap__chromeSpacer" aria-hidden="true" />

                {selectedLevel && (
                    <div
                        className={`storyBoardDetails storyBoardDetails--${selectedStatus}${
                            isUnderworldPage
                                ? " storyBoardDetails--underworld"
                                : ""
                        }`}
                        aria-live="polite"
                    >
                        <h3 className="storyBoardDetails__title">
                            Level {selectedLevel.id}: {selectedLevel.title}
                            {hardModeActive ? " (Hard)" : ""}
                        </h3>

                        {selectedStatus === "locked" ? (
                            <p
                                className="storyBoardDetails__lockHint"
                                role="status"
                            >
                                {getStoryLevelLockHint(
                                    selectedLevel.id,
                                    progress
                                )}
                            </p>
                        ) : (
                            <button
                                type="button"
                                className="homePrimaryButton storyBoardDetails__play"
                                onClick={handlePlaySelected}
                            >
                                {selectedStatus === "completed"
                                    ? "Replay Level"
                                    : "Play Level"}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
